import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Users, MessageSquare, ShoppingBag, TrendingUp, Gem } from 'lucide-react';
import api from '../../lib/api';
import { formatPrice } from '../../lib/utils';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalOrders: 0,
    totalCustomers: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    unreadMessages: 0,
    pendingStoneRequests: 0
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const [productsRes, ordersRes, customersRes, stoneRequestsRes] = await Promise.all([
        api.get('/products'),
        api.get('/admin/orders'),
        api.get('/admin/customers'),
        api.get('/admin/stone-requests').catch(() => ({ data: { requests: [] } }))
      ]);

      const orders = ordersRes.data;
      const totalRevenue = orders
        .filter(o => o.payment_status === 'paid')
        .reduce((sum, o) => sum + o.total, 0);
      const pendingOrders = orders.filter(o => o.status === 'pending').length;
      const pendingStoneRequests = (stoneRequestsRes.data.requests || []).filter(r => r.status === 'pending').length;

      setStats({
        totalProducts: productsRes.data.length,
        totalOrders: orders.length,
        totalCustomers: customersRes.data.customers?.length || 0,
        totalRevenue,
        pendingOrders,
        unreadMessages: 0,
        pendingStoneRequests
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Revenue',
      value: formatPrice(stats.totalRevenue, 'USD'),
      icon: TrendingUp,
      color: 'bg-green-50 text-green-600',
      link: '/admin/orders'
    },
    {
      title: 'Total Orders',
      value: stats.totalOrders,
      subtitle: `${stats.pendingOrders} pending`,
      icon: ShoppingBag,
      color: 'bg-blue-50 text-blue-600',
      link: '/admin/orders'
    },
    {
      title: 'Products',
      value: stats.totalProducts,
      icon: Package,
      color: 'bg-purple-50 text-purple-600',
      link: '/admin/products'
    },
    {
      title: 'Customers',
      value: stats.totalCustomers,
      icon: Users,
      color: 'bg-orange-50 text-orange-600',
      link: '/admin/customers'
    },
    {
      title: 'Stone Requests',
      value: stats.pendingStoneRequests,
      subtitle: 'pending',
      icon: Gem,
      color: 'bg-yellow-50 text-yellow-600',
      link: '/admin/stone-requests'
    },
    {
      title: 'Messages',
      value: stats.unreadMessages,
      subtitle: 'unread',
      icon: MessageSquare,
      color: 'bg-pink-50 text-pink-600',
      link: '/admin/messages'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="font-serif text-4xl mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Manage your gemstone business</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <div
              key={index}
              onClick={() => navigate(stat.link)}
              className="bg-white border border-gray-200 p-6 cursor-pointer hover:border-black transition-colors"
              data-testid={`stat-card-${index}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">{stat.title}</h3>
              <p className="text-3xl font-bold mb-1">{stat.value}</p>
              {stat.subtitle && (
                <p className="text-sm text-gray-500">{stat.subtitle}</p>
              )}
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="bg-white border border-gray-200 p-6">
          <h2 className="font-serif text-2xl mb-6">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <button
              onClick={() => navigate('/admin/products')}
              className="p-4 border border-gray-200 hover:border-black transition-colors text-left"
              data-testid="quick-action-products"
            >
              <Package className="w-8 h-8 mb-2" />
              <h3 className="font-medium mb-1">Manage Products</h3>
              <p className="text-sm text-gray-600">Add or edit gemstones</p>
            </button>

            <button
              onClick={() => navigate('/admin/orders')}
              className="p-4 border border-gray-200 hover:border-black transition-colors text-left"
              data-testid="quick-action-orders"
            >
              <ShoppingBag className="w-8 h-8 mb-2" />
              <h3 className="font-medium mb-1">View Orders</h3>
              <p className="text-sm text-gray-600">Process & ship orders</p>
            </button>

            <button
              onClick={() => navigate('/admin/customers')}
              className="p-4 border border-gray-200 hover:border-black transition-colors text-left"
              data-testid="quick-action-customers"
            >
              <Users className="w-8 h-8 mb-2" />
              <h3 className="font-medium mb-1">View Customers</h3>
              <p className="text-sm text-gray-600">Manage customer data</p>
            </button>

            <button
              onClick={() => navigate('/admin/stone-requests')}
              className="p-4 border border-gray-200 hover:border-black transition-colors text-left"
              data-testid="quick-action-stone-requests"
            >
              <Gem className="w-8 h-8 mb-2" />
              <h3 className="font-medium mb-1">Stone Requests</h3>
              <p className="text-sm text-gray-600">Customer gem requests</p>
            </button>

            <button
              onClick={() => navigate('/admin/messages')}
              className="p-4 border border-gray-200 hover:border-black transition-colors text-left"
              data-testid="quick-action-messages"
            >
              <MessageSquare className="w-8 h-8 mb-2" />
              <h3 className="font-medium mb-1">Messages</h3>
              <p className="text-sm text-gray-600">Customer inquiries</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;